import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Phone, Calculator, MapPin, FileText } from 'lucide-react';
import { getBlogCategories, getAllPosts } from '@/data/blogPosts';

interface BlogSidebarProps {
  currentCategory?: string;
}

const BlogSidebar: React.FC<BlogSidebarProps> = ({ currentCategory }) => {
  const { language } = useLanguage();
  const categories = getBlogCategories(language);
  const allPosts = getAllPosts();

  // Get recent posts (latest 4)
  const recentPosts = allPosts
    .sort((a, b) => new Date(b.published_date).getTime() - new Date(a.published_date).getTime())
    .slice(0, 4);

  return (
    <aside className="space-y-6">
      {/* CTA Card */}
      <Card className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-primary-foreground">
            {language === 'en' ? 'Get a Free Quote' : 'Cotización Gratis'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-primary-foreground/90">
            {language === 'en'
              ? 'Fresh sod & bulk materials delivered to your property.'
              : 'Césped fresco y materiales a granel entregados a tu propiedad.'
            }
          </p>
          <div className="space-y-2">
            <a href="tel:+19363200763">
              <Button variant="secondary" className="w-full justify-start" size="sm">
                <Phone className="mr-2 h-4 w-4" />
                (936) 320-0763
              </Button>
            </a>
            <Link to={`/${language}/${language === 'en' ? 'quote' : 'cotizar'}`}>
              <Button 
                variant="outline" 
                className="w-full justify-start border-primary-foreground/50 text-primary-foreground hover:bg-primary-foreground hover:text-primary" 
                size="sm"
              >
                <Calculator className="mr-2 h-4 w-4" />
                {language === 'en' ? 'Online Calculator' : 'Calculadora en Línea'}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {language === 'en' ? 'Categories' : 'Categorías'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {categories.map((category, index) => (
              <Link 
                key={index}
                to={`/${language}/blog?category=${encodeURIComponent(category.name)}`}
                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                  currentCategory === category.name 
                    ? 'bg-primary/10 text-primary' 
                    : 'hover:bg-muted'
                }`}
              >
                <span className="text-sm font-medium">{category.name}</span>
                <Badge variant="secondary" className="text-xs">
                  {category.count}
                </Badge>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Posts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {language === 'en' ? 'Recent Articles' : 'Artículos Recientes'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentPosts.map((post, index) => {
              const slug = language === 'en' ? post.slug_en : post.slug_es;
              const title = language === 'en' ? post.title_en : post.title_es;
              return (
                <Link 
                  key={index}
                  to={`/${language}/blog/${slug}`}
                  className="block group"
                >
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                    {title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {post.read_time} • {new Date(post.published_date).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', { month: 'short', day: 'numeric' })}
                  </p>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {language === 'en' ? 'Quick Links' : 'Enlaces Rápidos'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Link 
              to={`/${language}/${language === 'en' ? 'products' : 'productos'}`}
              className="flex items-center text-sm text-primary hover:underline"
            >
              <FileText className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Product Catalog' : 'Catálogo de Productos'}
            </Link>
            <Link 
              to={`/${language}/areas`}
              className="flex items-center text-sm text-primary hover:underline"
            >
              <MapPin className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Service Areas' : 'Áreas de Servicio'}
            </Link>
            <Link 
              to={`/${language}/${language === 'en' ? 'contact' : 'contacto'}`}
              className="flex items-center text-sm text-primary hover:underline"
            >
              <Phone className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Contact Us' : 'Contáctanos'}
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Location Card */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <MapPin className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <p className="font-medium text-sm">The Grass Depot LLC</p>
              <p className="text-sm text-muted-foreground">
                6920 FM 1488 RD<br />
                Magnolia, TX 77354
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'en' ? 'Mon-Sat: 7AM-6PM' : 'Lun-Sáb: 7AM-6PM'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </aside>
  );
};

export default BlogSidebar;
